import React from 'react';
import { BlockEmbed } from 'data/SingleArticle/types';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { Callout } from 'components/Callout/Callout';
import { PullQuote } from 'components/PullQuote/PullQuote';
import { ImageBlock } from 'components/ImageBlock/ImageBlock';
import { WistiaVideo } from 'components/Video/WistiaVideo';
import { YoutubeVideo } from 'components/Video/YoutubeVideo';
import { ZapTemplate } from 'components/ZapTemplate/ZapTemplate';
import { Table } from 'components/Table/Table';
import { baseMarksElementsConfiguration, richImageBody } from './configuration';
import { PersonaBasedCTA } from 'components/InArticleCTA/PersonaBasedCTA';
import { DemoCTA } from 'components/InArticleCTA/DemoCTA';
import { ExploreCTA } from 'components/InArticleCTA/ExploreCTA';
import { DownloadCTA } from 'components/InArticleCTA/DownloadCTA';
import { HowItWorksCTA } from 'components/InArticleCTA/HowItWorksCTA';
import { GenericCTA } from 'components/InArticleCTA/GenericCTA';

type InArticleCTALayoutOptions =
  | 'Explore CTA'
  | 'Demo CTA'
  | 'Download CTA'
  | 'Persona CTA'
  | 'How it works CTA'
  | 'Generic CTA';

type InArticleStrategy = {
  [key in InArticleCTALayoutOptions]: JSX.Element;
};

export function renderBlockEmbed(
  blockEmbed: BlockEmbed,
  persona: string | null,
) {
  if (blockEmbed.__typename === 'ZapTemplateComponent') {
    const {
      zapTemplateIDs,
      sys: { id },
    } = blockEmbed;
    if (!zapTemplateIDs || !zapTemplateIDs.length) {
      return null;
    }

    return (
      <ZapTemplate
        templateIds={zapTemplateIDs.filter(Boolean) as string[]}
        containerId={id}
      />
    );
  }

  if (blockEmbed.__typename === 'CalloutTopic') {
    const { body } = blockEmbed;

    if (!body) {
      return null;
    }

    return <Callout body={body} />;
  }

  if (blockEmbed.__typename === 'PullQuoteTopic') {
    const { quote, quoteByLine } = blockEmbed;

    if (!quote) {
      return null;
    }

    return <PullQuote quote={quote} quoteBy={quoteByLine} />;
  }

  if (blockEmbed.__typename === 'RichImageAsset') {
    const { image, describedImage, maxWidth, caption } = blockEmbed;
    const url = image?.url || '';
    const title = image?.title || '';
    const alt = describedImage || '';

    if (!image || !url) {
      return null;
    }

    const formattedCaption = documentToReactComponents(caption?.json, {
      renderNode: richImageBody,
      renderMark: baseMarksElementsConfiguration,
    });

    return (
      <ImageBlock image={{ maxWidth, alt, title, url }}>
        {formattedCaption}
      </ImageBlock>
    );
  }

  if (blockEmbed.__typename === 'VideoAsset') {
    const { videoId, videoProvider } = blockEmbed;

    if (
      !videoId ||
      (videoProvider?.name?.toLowerCase() !== 'wistia' &&
        videoProvider?.name?.toLowerCase() !== 'youtube')
    ) {
      return null;
    }

    if (videoProvider?.name === 'Wistia') {
      return <WistiaVideo videoId={videoId} />;
    }

    return <YoutubeVideo videoId={videoId} />;
  }

  if (blockEmbed.__typename === 'Table') {
    const { table } = blockEmbed;

    if (!table) {
      return null;
    }

    return <Table data={table.tableData} />;
  }

  if (blockEmbed.__typename === 'SectionAnchorAssembly') {
    const { htmlId } = blockEmbed;

    if (!htmlId) {
      return null;
    }

    return <div id={htmlId} />;
  }

  if (blockEmbed.__typename === 'InArticleCtaContainer') {
    const { fallbackCta: inArticleCta, variantsCtaCollection } = blockEmbed;

    if (
      !inArticleCta?.headline ||
      !inArticleCta.buttonText ||
      !inArticleCta.buttonUrl ||
      !inArticleCta.layout
    ) {
      return null;
    }

    /**
     * We're supposed to use Variants only for Persona based In Article CTAs
     * So, we assume that all the data will be persona related and we render the Persona InArticleCTA component.
     */
    if (variantsCtaCollection?.items.length) {
      const foundVariant = variantsCtaCollection.items.find(
        (item) => item?.persona?.slug === persona,
      );

      return (
        <PersonaBasedCTA
          inArticleCTA={foundVariant?.inArticleCta ?? inArticleCta}
        />
      );
    }

    const layout = inArticleCta.layout as InArticleCTALayoutOptions;

    const CTAComponentsMap: InArticleStrategy = {
      'How it works CTA': <HowItWorksCTA inArticleCTA={inArticleCta} />,
      'Download CTA': <DownloadCTA inArticleCTA={inArticleCta} />,
      'Explore CTA': <ExploreCTA inArticleCTA={inArticleCta} />,
      'Demo CTA': <DemoCTA inArticleCTA={inArticleCta} />,
      'Persona CTA': <PersonaBasedCTA inArticleCTA={inArticleCta} />,
      'Generic CTA': <GenericCTA inArticleCTA={inArticleCta} />,
    };

    return CTAComponentsMap[layout] || null;
  }

  /**
   * Render `null` if we don't find a matching component.
   */
  return null;
}
